import { flutterDirectPackages } from "@/data/flutterPackagesLicenses";
import { MIT_LICENSE_REFERENCE } from "@/data/mitLicenseReference";
import { TextPageLayout } from "@/pages/components/TextPageLayout";
import { DocumentHead } from "@/seo/DocumentHead";
import { PAGE_SEO } from "@/seo/pageMeta";

export function LicensesPage() {
  return (
    <TextPageLayout
      title="Open Source Licenses"
      lastUpdatedLine="Last updated: June 1, 2026"
    >
      <DocumentHead
        title={PAGE_SEO["/licenses"].title}
        description={PAGE_SEO["/licenses"].description}
        path="/licenses"
      />
      <h2 className="mb-4 text-3xl text-[#111111]">About open source</h2>
      <p className="mb-6 text-gray-700">
        The WasOK mobile app is built with{" "}
        <a
          href="https://flutter.dev"
          className="text-[#4CAF50] hover:underline"
          target="_blank"
          rel="noreferrer"
        >
          Flutter
        </a>{" "}
        and Dart. We&apos;re grateful to the developers and communities who
        create and maintain the open source packages we rely on. Below are the
        main <strong>direct</strong> libraries we integrate; the published app also bundles
        additional supporting libraries required by those packages. Where required,
        fuller notices may appear inside the app or in platform store disclosures.
      </p>

      <h2 className="mb-4 text-3xl text-[#111111]">
        Third-party packages (Flutter / Dart)
      </h2>

      <div className="mb-8 space-y-6">
        {flutterDirectPackages.map((pkg) => (
          <div
            key={pkg.name}
            className="rounded-2xl border border-gray-200 bg-white p-6"
          >
            <h3 className="mb-2 text-xl text-[#111111]">{pkg.name}</h3>
            <p className="mb-2 text-sm text-gray-500">Version: {pkg.version}</p>
            <p className="mb-3 text-gray-600">{pkg.description}</p>
            <p className="text-sm">
              <strong>License:</strong> {pkg.license}
              <br />
              <strong>Package:</strong>{" "}
              <a
                href={`https://pub.dev/packages/${pkg.name}`}
                className="text-[#4CAF50] hover:underline"
                target="_blank"
                rel="noreferrer"
              >
                pub.dev/packages/{pkg.name}
              </a>
            </p>
          </div>
        ))}
      </div>

      <h2 className="mb-4 text-3xl text-[#111111]">Flutter &amp; Dart</h2>
      <p className="mb-6 text-gray-700">
        The Flutter SDK, engine, and Dart SDK are subject to their own license
        terms (BSD 3-Clause and related notices). See{" "}
        <a
          href="https://github.com/flutter/flutter/blob/master/LICENSE"
          className="text-[#4CAF50] hover:underline"
          target="_blank"
          rel="noreferrer"
        >
          Flutter LICENSE
        </a>{" "}
        and{" "}
        <a
          href="https://github.com/dart-lang/sdk/blob/main/LICENSE"
          className="text-[#4CAF50] hover:underline"
          target="_blank"
          rel="noreferrer"
        >
          Dart SDK LICENSE
        </a>
        .
      </p>

      <h2 className="mb-4 text-3xl text-[#111111]">Other license types</h2>
      <p className="mb-6 text-gray-700">
        Packages above use MIT, BSD-style, Apache 2.0, or font licenses (e.g.
        SIL OFL) as indicated. Full legal text for each dependency is published
        with that package on pub.dev or in its source repository.
      </p>

      <h2 className="mb-4 text-3xl text-[#111111]">
        MIT License (reference text)
      </h2>
      <p className="mb-4 text-gray-700">
        Several listed packages are distributed under the MIT License. The
        canonical text below is included for convenience.
      </p>
      <div className="mb-8 overflow-x-auto rounded-2xl border border-gray-200 bg-gray-50 p-6 font-mono text-sm">
        <pre className="whitespace-pre-wrap">{MIT_LICENSE_REFERENCE}</pre>
      </div>

      <h2 className="mb-4 text-3xl text-[#111111]">Attribution</h2>
      <p className="mb-6 text-gray-700">
        We are deeply grateful to all open source contributors whose work makes
        WasOK possible.
      </p>

      <h2 className="mb-4 text-3xl text-[#111111]">Reporting license issues</h2>
      <p className="mb-6 text-gray-700">
        If you believe there is an issue with how we&apos;re using any open
        source software, or if you notice a missing attribution, please contact
        us at{" "}
        <a
          href="mailto:support@wasok.app"
          className="text-[#4CAF50] hover:underline"
        >
          support@wasok.app
        </a>
        .
      </p>
    </TextPageLayout>
  );
}
